import styled from "styled-components";
import Search from "shared/components/Search";
import useLessonListView from "features/teacher/hooks/lesson/useLessonListView";
import Refresh from "shared/components/Refresh";
import { hw, scrollbar } from "features/homework/homeworkUi";

interface ListViewProps {
    clickedItem: string;
    setClickedItem: React.Dispatch<React.SetStateAction<string>>;
}

const LessonAssignListView = ({ clickedItem, setClickedItem }: ListViewProps) => {
    const { search, searchedLists, onChange, onRefreshClick } = useLessonListView();

    return (
        <Container>
            <Header>
                <div>
                    <Title>학급 목록</Title>
                    <Sub>원생을 배정할 학급을 선택하세요.</Sub>
                </div>
                <Refresh onRefreshClick={onRefreshClick} />
            </Header>
            <Search placeholder="학급을 입력해 주세요" value={search} onChange={onChange} />
            <List>
                {searchedLists.length === 0 ? (
                    <Empty>
                        <EmptyTitle>{search ? "검색 결과가 없습니다" : "학급이 없습니다"}</EmptyTitle>
                        <EmptyText>
                            {search ? "다른 이름으로 다시 검색해 보세요." : "학급관리에서 학급을 먼저 생성하세요."}
                        </EmptyText>
                    </Empty>
                ) : (
                    searchedLists.map((item) => {
                        const id = item.uid || item.id;
                        return (
                            <Item
                                key={id}
                                $on={id === clickedItem}
                                onClick={() => setClickedItem(id)}
                            >
                                {item.name}
                            </Item>
                        );
                    })
                )}
            </List>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-width: 0;
    min-height: 0;
    height: 100%;
    padding: 20px 18px 16px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${hw.border};
    flex-shrink: 0;
`;

const Title = styled.h2`
    margin: 0;
    color: ${hw.text};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.02em;
`;

const Sub = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${hw.textMuted};
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 280px;
    border-radius: 12px;
    border: 1px solid ${hw.border};
    background: ${hw.surface};
    overflow-y: auto;
    overflow-x: hidden;
    ${scrollbar}
`;

const Item = styled.div<{ $on: boolean }>`
    padding: 12px 16px;
    border-bottom: 1px solid ${hw.border};
    color: ${hw.text};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    background: ${props => props.$on ? hw.brandSoft : hw.surface};
    box-shadow: ${props => props.$on ? `inset 3px 0 0 ${hw.brand}` : "none"};

    &:hover {
        background: ${props => props.$on ? hw.brandSoft : hw.bg};
    }

    &:last-child {
        border-bottom: none;
    }
`;

const Empty = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 36px 16px;
    text-align: center;
`;

const EmptyTitle = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: ${hw.text};
`;

const EmptyText = styled.div`
    font-size: 13px;
    color: ${hw.textMuted};
    line-height: 1.5;
`;

export default LessonAssignListView;
